import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import {
  CardHeadeWithActions,
  MwButton,
  MwInputText,
  MwSelector,
  MwSpinner,
  MwSpinnerButton,
} from "../components/ui";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import axios from "axios";
import { useTenant } from "../hooks";
import * as XLSX from "xlsx";
import LevelContext from "../utils/UserLevel";

export const TrialBalance = () => {
  const { t } = useTranslation("global");
  const [selectedP, setSelectedP] = useState(null);
  const tableRef = useRef(null);
  const lastAutoFetchKeyRef = useRef("");
  const { chart_account_level_limit } = useContext(LevelContext);
  const FormReducer = (state, action) => {
    switch (action.type) {
      case "ADD_TO_FORM":
        return { ...state, [action.field]: action.value };
      default:
        return state;
    }
  };

  // Initialize state based on whether selectedP is available
  const getInitialState = () => {
    if (selectedP?.startDateDisplay && selectedP?.endDateDisplay) {
      return {
        date_from: selectedP.startDateDisplay,
        date_to: selectedP.endDateDisplay,
        level: chart_account_level_limit
          ? {
              id: chart_account_level_limit,
              label:
                i18next.language === "ar"
                  ? `${chart_account_level_limit} المستوى`
                  : `Level ${chart_account_level_limit}`,
              value: chart_account_level_limit,
              lang: i18next.language,
            }
          : null,
      };
    }
    // Fallback to current year if selectedP is not available
    const now = new Date();
    const currentYear = now.getFullYear();
    const startOfYear = new Date(currentYear, 0, 1);
    const endOfYear = new Date(currentYear, 11, 31);

    const formatDate = (date) => {
      date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
      return date.toISOString().split("T")[0];
    };

    return {
      date_from: formatDate(startOfYear),
      date_to: formatDate(endOfYear),
      level: chart_account_level_limit
        ? {
            id: chart_account_level_limit,
            label:
              i18next.language === "ar"
                ? `${chart_account_level_limit} المستوى`
                : `Level ${chart_account_level_limit}`,
            value: chart_account_level_limit,
            lang: i18next.language,
          }
        : null,
    };
  };

  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState("");

  const { tenantId, branchSelected, companySelected, getCompanyById } =
    useTenant();
  const buildLevelOption = useCallback(
    (levelValue) => {
      const numericLevel = Number(levelValue || 0);
      if (!numericLevel) return null;

      return {
        id: numericLevel,
        label:
          i18next.language === "ar"
            ? `المستوى ${numericLevel}`
            : `Level ${numericLevel}`,
        value: numericLevel,
        lang: i18next.language,
      };
    },
    [t],
  );
  const [state, dispatch] = React.useReducer(FormReducer, getInitialState());
  const [Data, setData] = useState([]);

  // Load from localStorage and update form when period changes
  useEffect(() => {
    const updatePeriodData = () => {
      const storedPeriod = JSON.parse(localStorage.getItem("selectedPeriod"));
      if (storedPeriod) {
        setSelectedP(storedPeriod);
        dispatch({
          type: "ADD_TO_FORM",
          field: "date_from",
          value: storedPeriod.startDateDisplay,
        });
        dispatch({
          type: "ADD_TO_FORM",
          field: "date_to",
          value: storedPeriod.endDateDisplay,
        });
      }
    };

    updatePeriodData();
    window.addEventListener("storage", updatePeriodData);
    window.addEventListener("periodChanged", updatePeriodData);

    return () => {
      window.removeEventListener("storage", updatePeriodData);
      window.removeEventListener("periodChanged", updatePeriodData);
    };
  }, []);

  useEffect(() => {
    if (!chart_account_level_limit) return;

    const currentValue = Number(state.level?.value ?? state.level ?? 0);
    const maxLevel = Number(chart_account_level_limit);
    const normalizedValue =
      currentValue > 0 ? Math.min(currentValue, maxLevel) : maxLevel;
    const nextLevelOption = buildLevelOption(normalizedValue);

    if (!nextLevelOption) return;

    const needsSync =
      !state.level ||
      typeof state.level !== "object" ||
      Number(state.level?.value) !== nextLevelOption.value ||
      state.level?.label !== nextLevelOption.label ||
      state.level?.lang !== nextLevelOption.lang;

    if (needsSync) {
      dispatch({
        type: "ADD_TO_FORM",
        field: "level",
        value: nextLevelOption,
      });
    }
  }, [chart_account_level_limit, buildLevelOption, state.level]);

  const GetData = useCallback(async () => {
    const selectedLevelValue = Number(state.level?.value ?? state.level ?? 0);
    if (!state.date_from || !state.date_to) {
      setValidationError(t("common.required") || "التاريخ مطلوب");
      return;
    }

    const fromDate = new Date(state.date_from);
    const toDate = new Date(state.date_to);

    if (toDate < fromDate) {
      setValidationError(t("reports.endDateBeforeStart"));
      return;
    }

    if (!selectedLevelValue) {
      setValidationError(t("common.required") || "المستوى مطلوب");
      return;
    }

    setValidationError("");

    const payload = {
      date_from: state.date_from,
      date_to: state.date_to,
      level: selectedLevelValue,
      fin_year_id: selectedP?.id,
      branch_id: branchSelected?.value,
      tenant_id: tenantId,
      company_id: companySelected?.value,
    };
    try {
      setLoading(true);
      const res = await axios.post(
        `${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/reports/rep-02`,
        payload,
      );
      console.log(res);
      setData(res.data?.data || []);
    } catch (err) {
      console.log(err);
      setValidationError(t("reports.errorFetching"));
    } finally {
      setLoading(false);
    }
  }, [
    state.date_from,
    state.date_to,
    state.level,
    selectedP?.id,
    branchSelected?.value,
    tenantId,
    companySelected?.value,
    t,
  ]);

  const Levels = Array.from({ length: chart_account_level_limit }, (_, i) => ({
    id: i + 1,
    label: i18next.language === "ar" ? `المستوى ${i + 1}` : `Level ${i + 1}`,
    value: i + 1,
    lang: i18next.language,
  }));
  useEffect(() => {
    if (
      !selectedP?.id ||
      !tenantId ||
      !companySelected?.value ||
      !branchSelected?.value ||
      !state.date_from ||
      !state.date_to ||
      !state.level?.value
    ) {
      return;
    }

    const requestKey = [
      selectedP.id,
      tenantId,
      companySelected.value,
      branchSelected.value,
      state.date_from,
      state.date_to,
      state.level.value,
    ].join("|");

    if (lastAutoFetchKeyRef.current === requestKey) {
      return;
    }

    const timer = setTimeout(() => {
      lastAutoFetchKeyRef.current = requestKey;
      GetData();
    }, 250);

    return () => clearTimeout(timer);
  }, [
    GetData,
    selectedP?.id,
    tenantId,
    companySelected?.value,
    branchSelected?.value,
    state.date_from,
    state.date_to,
    state.level?.value,
  ]);

  const processedData = useMemo(() => {
    const grouped = {};

    Data.forEach((row) => {
      const key = row.account_no;

      if (!grouped[key]) {
        grouped[key] = {
          ...row,
          open_balance_de: Number(row.open_balance_de) || 0,
          open_balance_cr: Number(row.open_balance_cr) || 0,
          during_period_de: Number(row.during_period_de) || 0,
          during_period_cr: Number(row.during_period_cr) || 0,
          sum_all_de: Number(row.sum_all_de) || 0,
          sum_all_cr: Number(row.sum_all_cr) || 0,
          final_de: Number(row.final_de) || 0,
          final_cr: Number(row.final_cr) || 0,
        };
      } else {
        grouped[key].open_balance_de += Number(row.open_balance_de) || 0;
        grouped[key].open_balance_cr += Number(row.open_balance_cr) || 0;
        grouped[key].during_period_de += Number(row.during_period_de) || 0;
        grouped[key].during_period_cr += Number(row.during_period_cr) || 0;
        grouped[key].sum_all_de += Number(row.sum_all_de) || 0;
        grouped[key].sum_all_cr += Number(row.sum_all_cr) || 0;
        grouped[key].final_de += Number(row.final_de) || 0;
        grouped[key].final_cr += Number(row.final_cr) || 0;
      }
    });

    return Object.values(grouped);
  }, [Data]);
  // Calculate totals for all data
  const allTotals = useMemo(() => {
    return processedData.reduce(
      (acc, row) => ({
        open_balance_de:
          acc.open_balance_de + (Number(row.open_balance_de) || 0),
        open_balance_cr:
          acc.open_balance_cr + (Number(row.open_balance_cr) || 0),
        during_period_de:
          acc.during_period_de + (Number(row.during_period_de) || 0),
        during_period_cr:
          acc.during_period_cr + (Number(row.during_period_cr) || 0),
        sum_all_de: acc.sum_all_de + (Number(row.sum_all_de) || 0),
        sum_all_cr: acc.sum_all_cr + (Number(row.sum_all_cr) || 0),
        final_de: acc.final_de + (Number(row.final_de) || 0),
        final_cr: acc.final_cr + (Number(row.final_cr) || 0),
      }),
      {
        open_balance_de: 0,
        open_balance_cr: 0,
        during_period_de: 0,
        during_period_cr: 0,
        sum_all_de: 0,
        sum_all_cr: 0,
        final_de: 0,
        final_cr: 0,
      },
    );
  }, [processedData]);

  const formatNumber = (num) => {
    const numericValue = Math.abs(Number(num || 0));
    const formattedValue = new Intl.NumberFormat(
      i18next.language === "ar" ? "en-US" : "en-US",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    ).format(numericValue);

    return formattedValue;
  };
  const amountCellStyle = {
    direction: "ltr",
    unicodeBidi: "bidi-override",
  };

  // Excel Export with RTL support for Arabic
  const handleExportExcel = () => {
    if (processedData.length === 0) return;

    // Prepare worksheet data
    const headers = [
      t("reports.accountNumber"),
      t("reports.accountName"),
      t("reports.openingBalance") + " " + t("reports.dr"),
      t("reports.openingBalance") + " " + t("reports.cr"),
      t("reports.periodMovement") + " " + t("reports.dr"),
      t("reports.periodMovement") + " " + t("reports.cr"),
      t("reports.totals") + " " + t("reports.dr"),
      t("reports.totals") + " " + t("reports.cr"),
      t("reports.finalBalance") + " " + t("reports.dr"),
      t("reports.finalBalance") + " " + t("reports.cr"),
    ];

    const rows = processedData.map((row) => [
      row.account_no,
      row.account_name,
      formatNumber(row.open_balance_de),
      formatNumber(row.open_balance_cr),
      formatNumber(row.during_period_de),
      formatNumber(row.during_period_cr),
      formatNumber(row.sum_all_de),
      formatNumber(row.sum_all_cr),
      formatNumber(row.final_de),
      formatNumber(row.final_cr),
    ]);

    // Add totals row
    const totalsRow = [
      t("reports.total"),
      "",
      formatNumber(allTotals.open_balance_de),
      formatNumber(allTotals.open_balance_cr),
      formatNumber(allTotals.during_period_de),
      formatNumber(allTotals.during_period_cr),
      formatNumber(allTotals.sum_all_de),
      formatNumber(allTotals.sum_all_cr),
      formatNumber(allTotals.final_de),
      formatNumber(allTotals.final_cr),
    ];

    const worksheetData = [headers, ...rows, totalsRow];
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set RTL for Arabic, LTR for English
    if (i18next.language === "ar") {
      ws["!dir"] = "rtl";
    }

    // Set column widths
    ws["!cols"] = [
      { wch: 12 },
      { wch: 25 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "TrialBalance");
    XLSX.writeFile(
      wb,
      `TrialBalance_${state.date_from}_to_${state.date_to}.xlsx`,
    );
  };

  // Handle Print
  const handlePrint = () => {
    if (processedData.length === 0) {
      alert(t("reports.noDataPrint"));
      return;
    }

    // Prepare translation variables for print HTML
    const translations = {
      title: t("reports.trialBalance"),
      from: t("reports.from"),
      to: t("reports.to"),
      recordCount: t("reports.recordCount"),
      accountNumber: t("reports.accountNumber"),
      accountName: t("reports.accountName"),
      openingBalance: t("reports.openingBalance"),
      periodMovement: t("reports.periodMovement"),
      totals: t("reports.totals"),
      finalBalance: t("reports.finalBalance"),
      debit: t("reports.debit"),
      credit: t("reports.credit"),
      dr: t("reports.dr"),
      cr: t("reports.cr"),
      footerWebsite: t("reports.printFooterWebsite"),
      periodStartDate: t("reports.periodStartDate"),
      periodEndDate: t("reports.periodEndDate"),
      commercialRegisterNumber: t("setting.commercialRegisterNumber"),
      vatNumber: t("setting.vatNumber"),
    };
    const currentCompany = companySelected?.value
      ? getCompanyById(companySelected.value)
      : null;
    const companyDisplayName =
      companySelected?.name || companySelected?.label || "";
    const commercialRegisterNumber =
      currentCompany?.commercialRegisteration || "";
    const taxNumber = currentCompany?.vatNumber || "";
    const levelDisplay = state.level?.label || "";
    console.log(currentCompany, taxNumber);

    const printWindow = window.open("", "", "width=1400,height=900");
    const htmlContent = `
<!DOCTYPE html>
<html dir="${i18next.language === "ar" ? "rtl" : "ltr"}">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${translations.title}</title>

<style>
* { margin: 0; padding: 0; box-sizing: border-box; }

body {
  font-family: Arial, sans-serif;
  direction: ${i18next.language === "ar" ? "rtl" : "ltr"};
  margin: 10px;
  color: #000;
  background: #fff;
}

.report-shell {
  min-height: calc(100vh - 20px);
  display: flex;
  flex-direction: column;
}

.print-main {
  flex: 1 1 auto;
}

.header {
  text-align: center;
  margin-bottom: 15px;
}

.header h1 {
  font-size: 16px;
  margin-bottom: 8px;
  color: #000;
}

.header-company-section {
  margin: 10px 0 8px 0;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 6px;
  background-color: #f9f9f9;
}

.header-company-name {
  font-size: 12px;
  font-weight: bold;
  color: #1a1a1a;
  margin-bottom: 4px;
}

.header-company-details {
  display: flex;
  justify-content: center;
  gap: 15px;
  font-size: 9px;
  color: #333;
  flex-wrap: wrap;
}

.header-company-details span {
  margin: 0 2px;
}

.header-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 10px;
  gap: 10px;
  flex-wrap: wrap;
}

.header-level-section {
  display: flex;
  justify-content: center;
  gap: 15px;
  font-size: 10px;
  margin-top: 6px;
  flex-wrap: wrap;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  table-layout: fixed;
}

thead {
  display: table-header-group;
}

tbody {
  display: table-row-group;
}

th, td {
  border: 1px solid #333;
  padding: 5px 4px;
  text-align: center;
  font-size: 9px;
  color: #000;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

th {
  background-color: transparent;
  text-align: center;
  font-weight: bold;
}

tr.total-row {
  page-break-after: avoid;
  font-weight: bold;
}

.amount {
  text-align: center;
  padding: 5px 2px;
  direction: ltr;
  unicode-bidi: bidi-override;
}

.account-name-col {
  text-align: center;
  word-wrap: break-word;
  overflow-wrap: break-word;
  color: #000;
}

.report-footer {
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px solid #d8e2ef;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
}

.report-footer-logo {
  width: 76px;
  max-width: 76px;
  height: auto;
  object-fit: contain;
  display: block;
}

.footer-website {
  color: #000;
  font-size: 10px;
  letter-spacing: 0.4px;
  text-align: center;
  line-height: 1.1;
}

@media print {
  body {
    margin: 5px;
    font-size: 9px;
  }

  .report-shell {
    min-height: calc(100vh - 10px);
  }
  
  table {
    margin-top: 5px;
  }
  
  tbody tr:last-child {
    page-break-after: avoid;
  }
  
  thead {
    display: table-header-group;
  }
  
  tbody tr {
    page-break-inside: auto;
    page-break-after: auto;
  }
  
  tr.total-row {
    page-break-before: auto;
  }
}
</style>

</head>

<body>

<div class="report-shell">
  <div class="print-main">
    <div class="header">
      <h1>${translations.title}</h1>
      
      ${
        companyDisplayName
          ? `
      <div class="header-company-section">
        <div class="header-company-name">${companyDisplayName}</div>
        <div class="header-company-details">
          ${commercialRegisterNumber ? `<span>${translations.commercialRegisterNumber}: ${commercialRegisterNumber}</span>` : ""}
          ${taxNumber ? `<span>${translations.vatNumber}: ${taxNumber}</span>` : ""}
        </div>
      </div>
      `
          : ""
      }
      
      <div class="header-info">
        <span>${translations.periodStartDate}: ${state.date_from}</span>
        <span>${translations.periodEndDate}: ${state.date_to}</span>
      </div>
      
      ${
        levelDisplay
          ? `
      <div class="header-level-section">
        <span>${levelDisplay}</span>
      </div>
      `
          : ""
      }
    </div>

    <table>

    <thead>
    <tr>
      <th>${translations.accountNumber}</th>
      <th class="account-name-col">${translations.accountName}</th>
      <th colspan="2">${translations.openingBalance}</th>
      <th colspan="2">${translations.periodMovement}</th>
      <th colspan="2">${translations.totals}</th>
      <th colspan="2">${translations.finalBalance}</th>
    </tr>

    <tr>
      <th></th>
      <th></th>
      <th style="text-align: center;">${translations.dr}</th>
      <th style="text-align: center;">${translations.cr}</th>
      <th style="text-align: center;">${translations.dr}</th>
      <th style="text-align: center;">${translations.cr}</th>
      <th style="text-align: center;">${translations.dr}</th>
      <th style="text-align: center;">${translations.cr}</th>
      <th style="text-align: center;">${translations.dr}</th>
      <th style="text-align: center;">${translations.cr}</th>
    </tr>
    </thead>

    <tbody>
    ${processedData
      .map(
        (row) => `
    <tr>
      <td>${row.account_no}</td>
      <td class="account-name-col">${row.account_name}</td>

      <td class="amount">${formatNumber(row.open_balance_de)}</td>
      <td class="amount">${formatNumber(row.open_balance_cr)}</td>

      <td class="amount">${formatNumber(row.during_period_de)}</td>
      <td class="amount">${formatNumber(row.during_period_cr)}</td>

      <td class="amount">${formatNumber(row.sum_all_de)}</td>
      <td class="amount">${formatNumber(row.sum_all_cr)}</td>

      <td class="amount">${formatNumber(row.final_de)}</td>
      <td class="amount">${formatNumber(row.final_cr)}</td>
    </tr>
    `,
      )
      .join("")}
    <tr class="total-row">
      <td colspan="2" style="text-align: center; font-weight: bold;">${t("reports.total")}</td>
      <td class="amount">${formatNumber(allTotals.open_balance_de)}</td>
      <td class="amount">${formatNumber(allTotals.open_balance_cr)}</td>
      <td class="amount">${formatNumber(allTotals.during_period_de)}</td>
      <td class="amount">${formatNumber(allTotals.during_period_cr)}</td>
      <td class="amount">${formatNumber(allTotals.sum_all_de)}</td>
      <td class="amount">${formatNumber(allTotals.sum_all_cr)}</td>
      <td class="amount" style="font-weight: bold;">${formatNumber(allTotals.final_de)}</td>
      <td class="amount" style="font-weight: bold;">${formatNumber(allTotals.final_cr)}</td>
    </tr>
    </tbody>

    </table>
  </div>

  <div class="report-footer">
    <img class="report-footer-logo" src="/cube.JPG" alt="${translations.title}" />
    <div class="footer-website">${translations.footerWebsite}</div>
  </div>
</div>

</body>
</html>
`;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };
  if (!chart_account_level_limit) {
    return <MwSpinner />;
  }
  return (
    <section className="p-1 font-normal">
      <CardHeadeWithActions
        title={t("reports.trialBalance")}
        icon={"/assets/icons/balance.png"}
      >
        {processedData.length > 0 && (
          <div className="flex gap-2">
            <MwButton
              children={t("reports.print")}
              onClick={handlePrint}
              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm"
            />
            <MwButton
              children={t("reports.exportExcel")}
              onClick={handleExportExcel}
              className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm"
            />
          </div>
        )}
      </CardHeadeWithActions>

      {/* filter */}
      <div className="bg-white rounded-md p-2 mt-3">
        <div className="grid gap-2 grid-cols-4">
          <MwInputText
            value={state.date_from}
            onChange={(e) =>
              dispatch({
                type: "ADD_TO_FORM",
                field: "date_from",
                value: e.target.value,
              })
            }
            invalid={validationError ? true : false}
            invalidMsg={validationError}
            inputType="date"
            label={t("reports.start_date")}
          />
          <MwInputText
            value={state.date_to}
            onChange={(e) =>
              dispatch({
                type: "ADD_TO_FORM",
                field: "date_to",
                value: e.target.value,
              })
            }
            inputType="date"
            label={t("reports.end_date")}
          />
          <MwSelector
            _data={Levels}
            dataType={"labelValue"}
            withSearch={false}
            withAddNew={false}
            label={t("reports.select")}
            selectedItem={state.level}
            setSelectedItem={(selected) => {
              dispatch({
                type: "ADD_TO_FORM",
                field: "level",
                value: selected,
              });
            }}
          />
          {loading && (
            <div className="flex   justify-start items-end">
              <MwSpinnerButton />
            </div>
          )}
        </div>
      </div>

      {/* table */}
      {processedData.length > 0 && (
        <div ref={tableRef} className="bg-white shadow-md rounded-md p-3 mt-3">
          {/* header info */}
          <div className="flex justify-around text-sm text-gray-600 mb-3">
            <span>
              {t("reports.periodStartDate")}: {state.date_from || "---"}
            </span>
            <span>
              {t("reports.periodEndDate")}: {state.date_to || "---"}
            </span>
          </div>

          <div
            className="rounded-md border border-gray-200"
            style={{
              display: "flex",
              flexDirection: "column",
              maxHeight: "calc(100vh - 298px)",
            }}
          >
            {/* Header */}
            <div className=" !bg-gray-800 overflow-x-auto flex-shrink-0 w-[99.12%]">
              <table
                className="w-full  text-[13px] border-collapse"
                style={{ tableLayout: "fixed" }}
              >
                <colgroup>
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                </colgroup>

                <thead className="bg-slate-100  text-[#261759] ">
                  <tr className="text-center border-b-2 border-gray-300 ">
                    <th rowSpan="2" className="p-2 border align-middle">
                      {t("reports.accountNumber")}
                    </th>
                    <th
                      rowSpan="2"
                      className="p-2 border text-start align-middle"
                    >
                      {t("reports.accountName")}
                    </th>

                    <th colSpan="2" className="p-2 border">
                      {t("reports.openingBalance")}
                    </th>
                    <th colSpan="2" className="p-2 border">
                      {t("reports.periodMovement")}
                    </th>
                    <th colSpan="2" className="p-2 border">
                      {t("reports.totals")}
                    </th>
                    <th colSpan="2" className="p-2 border">
                      {t("reports.finalBalance")}
                    </th>
                  </tr>

                  <tr className="text-center border-b-2 border-gray-300">
                    <th className="p-2 border">{t("reports.debit")}</th>
                    <th className="p-2 border">{t("reports.credit")}</th>
                    <th className="p-2 border">{t("reports.debit")}</th>
                    <th className="p-2 border">{t("reports.credit")}</th>
                    <th className="p-2 border">{t("reports.debit")}</th>
                    <th className="p-2 border">{t("reports.credit")}</th>
                    <th className="p-2 border">{t("reports.debit")}</th>
                    <th className="p-2 border">{t("reports.credit")}</th>
                  </tr>
                </thead>
              </table>
            </div>

            {/* Body - scrollable */}
            <div
              className="overflow-auto flex-1"
              style={{ minHeight: 0, overflowX: "auto" }}
            >
              <table
                className="w-full text-[13px] border-collapse"
                style={{ tableLayout: "fixed" }}
              >
                <colgroup>
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                </colgroup>

                <tbody>
                  {processedData.map((row, index) => (
                    <tr
                      key={index}
                      className="text-center text-[#261759] hover:bg-gray-50 border-b"
                    >
                      <td className="p-2 border text-[#261759] font-medium">
                        {row.account_no}
                      </td>
                      <td className="p-2 border text-start">
                        {row.account_name}
                      </td>

                      <td className="p-2 border" style={amountCellStyle}>
                        {formatNumber(row.open_balance_de)}
                      </td>
                      <td className="p-2 border" style={amountCellStyle}>
                        {formatNumber(row.open_balance_cr)}
                      </td>

                      <td className="p-2 border" style={amountCellStyle}>
                        {formatNumber(row.during_period_de)}
                      </td>
                      <td className="p-2 border" style={amountCellStyle}>
                        {formatNumber(row.during_period_cr)}
                      </td>

                      <td className="p-2 border" style={amountCellStyle}>
                        {formatNumber(row.sum_all_de)}
                      </td>
                      <td className="p-2 border" style={amountCellStyle}>
                        {formatNumber(row.sum_all_cr)}
                      </td>

                      <td
                        className="p-2 border font-semibold"
                        style={amountCellStyle}
                      >
                        {formatNumber(row.final_de)}
                      </td>
                      <td
                        className="p-2 border font-semibold"
                        style={amountCellStyle}
                      >
                        {formatNumber(row.final_cr)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer - Total (fixed at bottom) */}
            <div className="overflow-x-auto flex-shrink-0 bg-slate-50 border-t-2 w-[99.12%] border-gray-300">
              <table
                className="w-full text-[13px] border-collapse"
                style={{ tableLayout: "fixed" }}
              >
                <colgroup>
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "9%" }} />
                </colgroup>

                <tbody>
                  <tr className="bg-slate-50 text-[#261759] font-bold text-center">
                    <td className="p-2 border text-center align-middle"></td>

                    <td className="p-2 border text-center align-middle">
                      {t("reports.total")}
                    </td>

                    <td className="p-2 border" style={amountCellStyle}>
                      {formatNumber(allTotals.open_balance_de)}
                    </td>
                    <td className="p-2 border" style={amountCellStyle}>
                      {formatNumber(allTotals.open_balance_cr)}
                    </td>

                    <td className="p-2 border" style={amountCellStyle}>
                      {formatNumber(allTotals.during_period_de)}
                    </td>
                    <td className="p-2 border" style={amountCellStyle}>
                      {formatNumber(allTotals.during_period_cr)}
                    </td>

                    <td className="p-2 border" style={amountCellStyle}>
                      {formatNumber(allTotals.sum_all_de)}
                    </td>
                    <td className="p-2 border" style={amountCellStyle}>
                      {formatNumber(allTotals.sum_all_cr)}
                    </td>

                    <td className="p-2 border" style={amountCellStyle}>
                      {formatNumber(allTotals.final_de)}
                    </td>
                    <td className="p-2 border" style={amountCellStyle}>
                      {formatNumber(allTotals.final_cr)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
